import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { membershipSchema, type Membership } from "./schemas";

export type MembershipState =
  | { status: "loading" }
  | { status: "not-member" }
  | { status: "error"; error: string }
  | { status: "success"; data: Membership };

/**
 * P3: `events/{eventId}/members/{uid}` by doc id.
 *
 * Requires:
 * - Rule: `get: isSelf(uid) || isEventAdmin`
 */
export function useMembership(
  eventId: string,
  uid: string,
  refetchKey = 0,
): MembershipState {
  const [state, setState] = useState<MembershipState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    getDoc(doc(db, "events", eventId, "members", uid))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setState({ status: "not-member" });
          return;
        }
        const result = membershipSchema.safeParse(snap.data());
        setState(
          result.success
            ? { status: "success", data: result.data }
            : { status: "not-member" },
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            err instanceof Error ? err.message : "Failed to check membership",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, uid, refetchKey]);

  return state;
}
